import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  Alert,
  Button,
  FormField,
  GoogleButton,
  Input,
  PasswordInput,
  LanguageSwitcher,
  Logo,
  ThemeToggle,
  useDevCredentials,
} from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api, googleClientId } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { SignUpForm, type GooglePrefill } from '../components/SignUpForm.js';

export function SignInPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const { signIn, signInWithGoogle } = useAuth();

  /*
   * `?signup` opens straight on the form rather than on the sign-in
   * screen. The public site's "list your car" page links in that way, and
   * an owner who has just decided to join should not first be asked to
   * sign in to an account they do not have. Read once, as the initial
   * state: the toggle below owns the mode from then on.
   */
  const [params] = useSearchParams();
  const [mode, setMode] = useState<'signIn' | 'signUp'>(params.has('signup') ? 'signUp' : 'signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [googleCredential, setGoogleCredential] = useState<GooglePrefill | null>(null);
  const [busy, setBusy] = useState(false);

  // The dev login panel (`@zuruflex/ui` DevAccounts) types into these fields
  // and submits, rather than signing in behind the form's back, so what
  // lands here is exactly what a person would have typed and pressed. The
  // credentials go straight to `signIn` rather than through the state just
  // set, which this render has not seen yet.
  useDevCredentials((credentials) => {
    setEmail(credentials.email);
    setPassword(credentials.password);
    setMode('signIn');
    void attempt(() => signIn(credentials.email, credentials.password));
  });

  const { data: methods } = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.authConfig(),
    staleTime: Infinity,
  });
  const googleEnabled = Boolean(googleClientId) && methods?.google !== false;

  /**
   * `google` is only ever set for the Google button, and only matters on
   * the NOT_FOUND path, where it fills the sign-up form in and finishes it.
   */
  async function attempt(action: () => Promise<void>, google?: GooglePrefill) {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (cause) {
      /**
       * NOT_FOUND from the Google route means "no account for this
       * address" rather than a failure. The signed-in-with-Google person
       * is one form away from having one, so show it instead of an error.
       */
      if (cause instanceof ZuruflexApiError && cause.status === 404) {
        setNotice(t('signIn.googleNoAccount'));
        setGoogleCredential(google ?? null);
        setMode('signUp');
        return;
      }
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-between">
          <Logo className="h-8" />
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageSwitcher
              value={i18n.language}
              onChange={(locale) => void i18n.changeLanguage(locale)}
              variant="compact"
              label={t('common:field.language')}
            />
          </div>
        </div>

        {mode === 'signUp' ? (
          <SignUpForm
            notice={notice}
            prefill={googleCredential}
            googleEnabled={googleEnabled}
            onBackToSignIn={() => {
              setNotice(null);
              setError(null);
              setGoogleCredential(null);
              setMode('signIn');
            }}
          />
        ) : (
          <>
            <h1 className="mt-8 text-xl font-semibold text-text">{t('signIn.title')}</h1>
            <p className="mt-1 text-sm text-text-muted">{t('signIn.subtitle')}</p>

            {error ? (
              <Alert variant="danger" className="mt-6">
                {error}
              </Alert>
            ) : null}

            <form
              className="mt-6 flex flex-col gap-4"
              onSubmit={(event: FormEvent) => {
                event.preventDefault();
                void attempt(() => signIn(email, password));
              }}
            >
              <FormField label={t('common:field.email')} required>
                {(field) => (
                  <Input
                    {...field}
                    type="email"
                    value={email}
                    autoComplete="username"
                    autoFocus
                    required
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </FormField>

              <FormField label={t('common:field.password')} required>
                {(field) => (
                  <PasswordInput
                    {...field}
                    value={password}
                    autoComplete="current-password"
                    required
                    onChange={(event) => setPassword(event.target.value)}
                    showLabel={t('common:action.showPassword')}
                    hideLabel={t('common:action.hidePassword')}
                  />
                )}
              </FormField>

              <Button type="submit" disabled={busy} className="mt-1 w-full">
                {busy ? t('common:state.working') : t('common:action.signIn')}
              </Button>
            </form>

            {googleEnabled ? (
              <>
                <div className="my-6 flex items-center gap-3">
                  <span className="h-px flex-1 bg-border-default" />
                  <span className="text-xs uppercase tracking-wide text-text-muted">
                    {t('common:word.or')}
                  </span>
                  <span className="h-px flex-1 bg-border-default" />
                </div>
                <GoogleButton
                  clientId={googleClientId}
                  locale={i18n.language}
                  onCredential={(accessToken, profile) =>
                    void attempt(() => signInWithGoogle({ accessToken }), { accessToken, profile })
                  }
                />
              </>
            ) : null}

            <p className="mt-8 text-center text-sm text-text-muted">
              {t('signIn.noAccount')}{' '}
              <button
                type="button"
                className="font-semibold text-text underline underline-offset-2"
                onClick={() => setMode('signUp')}
              >
                {t('signIn.createOne')}
              </button>
            </p>
          </>
        )}
      </div>
    </main>
  );
}
